import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@worldcoo/ui/components/accordion';
import { motion } from 'motion/react';
import { useFaqCategories } from './-components/faq-data';
import { MainSectionHeader, MainSectionContent } from './-components/main-section';

export const Route = createFileRoute('/ngos/faq')({
	component: FaqContent,
});

function FaqContent() {
	const { t } = useLingui();
	const faqCategories = useFaqCategories();
	const [activeCategory, setActiveCategory] = useState(faqCategories[0]?.id ?? 'projects');
	const scrollRef = useRef<HTMLDivElement>(null);
	const isScrollingToRef = useRef(false);

	const scrollToCategory = (categoryId: string) => {
		setActiveCategory(categoryId);
		const element = document.getElementById(`faq-${categoryId}`);
		const container = scrollRef.current;
		if (element && container) {
			isScrollingToRef.current = true;
			element.scrollIntoView({ behavior: 'smooth', block: 'start' });
			// Reset the flag after scrolling finishes
			setTimeout(() => {
				isScrollingToRef.current = false;
			}, 600);
		}
	};

	const handleScroll = useCallback(() => {
		if (isScrollingToRef.current) return;
		const container = scrollRef.current;
		if (!container) return;

		for (const category of faqCategories) {
			const element = document.getElementById(`faq-${category.id}`);
			if (element) {
				const rect = element.getBoundingClientRect();
				const containerRect = container.getBoundingClientRect();
				if (rect.top >= containerRect.top && rect.top < containerRect.top + containerRect.height / 2) {
					setActiveCategory(category.id);
					break;
				}
			}
		}
	}, [faqCategories]);

	useEffect(() => {
		const container = scrollRef.current;
		if (!container) return;
		container.addEventListener('scroll', handleScroll, { passive: true });
		return () => container.removeEventListener('scroll', handleScroll);
	}, [handleScroll]);

	return (
		<>
			<MainSectionHeader
				title={
					<h1 className="text-foreground text-2xl font-medium tracking-[-0.2px]">{t`Frequently Asked Questions`}</h1>
				}
			/>

			<MainSectionContent ref={scrollRef}>
				<div className="flex flex-1 gap-8 p-6">
					<FaqCategoryNav
						categories={faqCategories}
						activeCategory={activeCategory}
						onCategoryClick={scrollToCategory}
					/>

					<div className="flex max-w-[603px] flex-1 flex-col gap-4">
						{faqCategories.map((category, categoryIndex) => (
							<section key={category.id} id={`faq-${category.id}`}>
								<div className="shadow-warm rounded-2xl border bg-white px-8 pt-8 pb-10">
									<h2 className="mb-6 text-lg leading-[1.5] font-medium text-[#292d32]">{category.title}</h2>
									<Accordion
										type="single"
										collapsible
										defaultValue={categoryIndex === 0 ? category.items[0]?.question : undefined}
										className="flex flex-col"
									>
										{category.items.map((item, index) => (
											<AccordionItem
												key={item.question}
												value={item.question}
												className={index < category.items.length - 1 ? 'border-b pb-4' : 'border-none'}
											>
												<AccordionTrigger className="py-4 text-base leading-[1.5] font-medium tracking-[-0.2px] text-[#35433f] no-underline hover:no-underline [&>svg]:size-6 [&>svg]:text-[#35433f]">
													{item.question}
												</AccordionTrigger>
												<AccordionContent className="text-sm leading-[1.5] tracking-[-0.2px] text-[#666]">
													{item.answer}
												</AccordionContent>
											</AccordionItem>
										))}
									</Accordion>
								</div>
							</section>
						))}

						{/* Contact CTA */}
						<div className="border-portal-border rounded-2xl border bg-[#fff7ed] p-8">
							<p className="text-base leading-[1.5] font-medium tracking-[-0.2px] text-[#35433f]">
								{t`Didn't find the answer you were looking for?`}
							</p>
							<p className="mt-2 text-sm leading-[1.5] text-[#666]">
								{t`Send us an email at`}{' '}
								<a href="mailto:contact@worldcoo.com" className="font-semibold hover:underline">
									contact@worldcoo.com
								</a>{' '}
								{t`or call us at`}{' '}
								<a href="tel:+34936110104" className="font-semibold hover:underline">
									(+34) 936 11 01 04
								</a>
							</p>
						</div>
					</div>
				</div>
			</MainSectionContent>
		</>
	);
}

function FaqCategoryNav(props: {
	categories: Array<{ id: string; title: string }>;
	activeCategory: string;
	onCategoryClick: (categoryId: string) => void;
}) {
	return (
		<nav className="sticky top-6 flex h-fit flex-col">
			<div className="relative border-l border-[#f28e41]/20">
				{/* Animated active indicator */}
				<motion.div
					className="absolute left-[-1px] w-[2px] bg-[#f28e41]"
					layoutId="faq-category-indicator"
					animate={{
						top: `${props.categories.findIndex((c) => c.id === props.activeCategory) * 36}px`,
					}}
					style={{ height: 36 }}
					transition={{ type: 'spring', stiffness: 500, damping: 35 }}
				/>
				{props.categories.map((category) => (
					<button
						key={category.id}
						type="button"
						onClick={() => props.onCategoryClick(category.id)}
						className={`block w-full px-4 py-2 text-left text-sm tracking-[-0.2px] transition-colors ${
							props.activeCategory === category.id ? 'text-foreground font-medium' : 'text-foreground'
						}`}
					>
						{category.title}
					</button>
				))}
			</div>
		</nav>
	);
}
